import { JSONSchema7WithErrors } from '@cibo/core'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
dayjs.extend(customParseFormat)

/**
 * When schema indicates format:date and backend sends ISO date, transform to 'YYYY-MM-DD'in place.
 */
// eslint-disable-next-line visual/complexity
export function applyDateTransforms(schema: JSONSchema7WithErrors, data: any): any {
  if (!schema) return data

  if (schema.type === 'array' && Array.isArray(data)) {
    const itemSchema = schema.items || {}
    return data.map(item => applyDateTransforms(itemSchema as JSONSchema7WithErrors, item))
  }

  // If data is an array but schema is an object with properties that might include arrays,
  // we still want to treat the data as an array when schema.items isn't present.
  if (Array.isArray(data) && schema.type !== 'array') {
    // No specific array schema, so recurse using the same schema for each element.
    return data.map(item => applyDateTransforms(schema, item))
  }

  // If schema describes an object with properties, recurse into each property
  if (
    schema.type === 'object' &&
    typeof data === 'object' &&
    data !== null &&
    !Array.isArray(data)
  ) {
    const result = Array.isArray(data) ? [...data] : { ...data }
    const props = schema.properties || {}

    // For each declared property in the schema, transform if present in data
    for (const key of Object.keys(props)) {
      const propSchema = props[key]
      if (!(key in data) || data[key] == null) continue

      result[key] = applyDateTransforms(propSchema, data[key])
    }

    return result
  }

  const typeIncludesString =
    schema.type === 'string' || (Array.isArray(schema.type) && schema.type.includes('string'))

  // If schema expects a string with date format, and the data is a string, parse & format it
  if (typeIncludesString && schema.format === 'date' && typeof data === 'string') {
    const parsed = dayjs(data)

    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : data
  }

  // If none of the above matched, return the value unchanged
  return data
}
